import { Outlet } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";

import { Suspense, useEffect } from "react";
import { Alert, Snackbar } from "@mui/material";
import { LicenseInfo } from "@mui/x-license-pro";
import "./App.css";
import { SIMPLModal } from "./common-ui/SIMPLModal";
import {
  selectToastAutoHideDuration,
  selectToastMessage,
  selectToastShow,
  selectToastStyle,
  setShowToast,
  setToastAutoHideDuration
} from "./common-ui/toastSlice";
import { useAppSelector } from "./app/hooks";
import {
  selectModalContent,
  selectModalHideCloseButton
} from "./common-ui/modalSlice";
import { useDispatch } from "react-redux";
import { useSponsorBranding } from "./features/Sponsors/sponsorUtils";
import { selectCurrentUser } from "./features/Users/usersSlice";
import { BrandingContext } from "./common-ui/BrandingContext";

// MUI expects this to be public/checked-in:
// https://mui.com/x/introduction/licensing/#license-key-security
LicenseInfo.setLicenseKey(
  "cbff915d6f86c72684b9fd4c21c02ffcTz0xMzM3ODMsRT0xODI0MzM1OTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9aW5pdGlhbCxLVj0y"
);

function App() {
  const showToast = useAppSelector(selectToastShow);
  const toastAutoHideDuration = useAppSelector(selectToastAutoHideDuration);
  const toastStyle = useAppSelector(selectToastStyle);
  const toastMessage = useAppSelector(selectToastMessage);
  const modalContent = useAppSelector(selectModalContent);
  const hideModalClose = useAppSelector(selectModalHideCloseButton);
  const currentUser = useAppSelector(selectCurrentUser);
  const {
    theme,
    logoFullMonoURL,
    logoSquareMonoURL,
    name,
    status: brandingStatus,
    error: brandingError,
    getSponsorBranding
  } = useSponsorBranding();
  const dispatch = useDispatch();

  const handleToastClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(setShowToast(false));
    dispatch(setToastAutoHideDuration(5000));
  };

  useEffect(() => {
    if (currentUser) {
      getSponsorBranding(currentUser);
    }
  }, [currentUser]);

  return (
    <Suspense fallback="Loading...">
      {/* See theme.ts for info about the theme/ThemeProvider */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={{ ...theme }}>
          <BrandingContext.Provider
            value={{
              theme,
              logoFullMonoURL,
              logoSquareMonoURL,
              name,
              status: brandingStatus,
              error: brandingError
            }}
          >
            <div className="App">
              <Snackbar
                open={showToast}
                autoHideDuration={toastAutoHideDuration}
                onClose={handleToastClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={handleToastClose}
                  severity={toastStyle}
                  variant="filled"
                  sx={{ width: "100%", fontSize: "1rem", boxShadow: 6 }}
                >
                  {toastMessage}
                </Alert>
              </Snackbar>
              <SIMPLModal hideCloseButton={hideModalClose}>
                {modalContent}
              </SIMPLModal>
              <Outlet />
            </div>
          </BrandingContext.Provider>
        </ThemeProvider>
      </LocalizationProvider>
    </Suspense>
  );
}

export default App;
